import type {
    FloatingContext,
    Placement,
    UseFloatingOptions,
    UseInteractionsReturn,
    UseListNavigationProps,
    ReferenceType,
    UseHoverProps,
    UseDismissProps, UseClickProps,
} from '@floating-ui/react';

import {
    useFloatingParentNodeId,
    flip,
    offset,
    safePolygon,
    shift,
    size,
    useClick,
    useDismiss,
    useFloating as useFloatingHook,
    useFloatingNodeId,
    useTypeahead,
    useHover,
    useInteractions, useListNavigation, useRole, useFocus,
} from '@floating-ui/react';
import {useControlledState} from '@pb/kit-hooks-use-controlled-state';

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import React, {useRef} from 'react';
import type {UseArrowProps, UseArrowReturnType} from './use-arrow';
import {useComponentDidUpdate} from '@pb/kit-hooks-use-component-did-update';

import {useArrow} from './use-arrow';


import type {ComponentProps} from 'react';
import type {SetShown} from '../typedefs';
import type {UseUpdateOptions} from './use-update';
import {useUpdate} from './use-update';

const Strategies = {
    ABSOLUTE: 'absolute',
    FIXED: 'fixed',
} as const;

type _UseFloatingOptions = UseFloatingOptions;

export interface UseFloatingProps extends _UseFloatingOptions {
    /**
     * Floating UI positioning strategy.
     * @deprecated It will be removed in 6 months 03-12-2026. Use strategy instead.
     */
    type?: 'ABSOLUTE' | 'FIXED',

    /**
     * Controlled open state. When provided, the hook does not manage its own
     * visibility — pair with `setShown` to form a controlled pattern.
     */
    shown?: boolean,

    /**
     * Setter for the controlled `shown` state. Required when `shown` is provided.
     */
    setShown?: SetShown,

    /**
     * Maximum height of the floating panel in pixels. Enforced via the Floating UI
     * `size` middleware; when the available viewport height is smaller, the panel
     * is capped at the available height instead.
     */
    maxHeight?: number,

    /**
     * Maximum width of the floating panel in pixels. When `equalWidth` is `true`,
     * this value is ignored and the panel width matches the anchor width.
     */
    maxWidth?: number,

    /**
     * Vertical offset (in px) applied along the main axis between the anchor and the
     * floating panel. Positive values increase the gap; defaults to `0`. The arrow
     * height is added on top of this value automatically when `arrowEnabled` is `true`.
     */
    verticalOffset?: number,

    /**
     * Horizontal offset (in px) applied along the cross axis. Positive values move
     * the panel to the right (for bottom/top placements). Defaults to `0`.
     */
    horizontalOffset?: number,

    /**
     * WAI-ARIA role set on the floating element via `useRole`. Affects which ARIA
     * attributes are applied to the anchor and the panel.
     */
    role?: 'tooltip' | 'dialog' | 'alertdialog' | 'menu' | 'listbox' | 'grid' | 'tree' | 'select' | 'label' | 'combobox',

    /**
     * When `true`, enables Floating UI's `useListNavigation` so arrow-key events
     * move focus among registered list items. Required for keyboard-navigable lists.
     */
    listNavigationEnabled?: boolean,

    /**
     * Ordered list of Floating UI placements the panel may occupy. The first
     * placement that fits the viewport is used. Passed as `fallbackPlacements` to
     * the `flip` middleware.
     */
    allowedPlacements?: Placement[],

    /**
     * Additional Floating UI middleware appended after the default stack
     * (`offset`, `flip`, `shift`, `size`, arrow). Use to add `hide`, custom
     * transforms, or override built-in middleware behavior.
     */
    middlewares?: UseFloatingOptions['middleware'],

    /**
     * When `true`, renders an arrow pointing from the floating panel toward the
     * anchor. Arrow size is reflected in `verticalOffset` automatically.
     */
    arrowEnabled?: boolean,

    /** When `true`, enables `useFocus` so the panel opens on keyboard focus of the anchor. */
    focusEnabled?: boolean,
    /** When `true`, enables `useClick` so the panel toggles on click of the anchor. */
    clickEnabled?: boolean,
    /** When `true`, enables `useHover` so the panel opens on pointer enter of the anchor. */
    hoverEnabled?: boolean,
    /** When `true`, enables `useTypeahead` for character-key item matching inside the list. */
    typeaheadEnabled?: boolean,

    /**
     * Called whenever the panel opens or closes. Receives `true` when opening,
     * `false` when closing. Fires via `useComponentDidUpdate` after the state
     * settles, so it does not run on mount.
     */
    onToggle?: (shown: boolean) => unknown,

    /**
     * When `true`, the floating panel width is set to match the anchor element's
     * width exactly. Takes precedence over `maxWidth`.
     */
    equalWidth?: boolean,

    /**
     * Props forwarded to Floating UI's `useListNavigation`. Use to configure
     * `loop`, `orientation`, `cols`, `activeIndex`, `onNavigate`, etc.
     */
    listNavigationProps?: Partial<UseListNavigationProps>,

    /**
     * Props forwarded to Floating UI's `useHover`. Use to configure `delay`,
     * `handleClose` strategy, or `mouseOnly`.
     * See {@link https://floating-ui.com/docs/useHover}.
     */
    hoverProps?: Partial<UseHoverProps>,

    /** Props forwarded to the internal `useArrow` hook. */
    arrowProps?: UseArrowProps,

    /**
     * Props forwarded to Floating UI's `useDismiss`. Use to configure
     * `outsidePress`, `referencePress`, `escapeKey`, etc.
     * See {@link https://floating-ui.com/docs/useDismiss}.
     */
    dismissProps?: UseDismissProps,

    /**
     * Props forwarded to Floating UI's `useClick`. Use to configure `toggle`,
     * `event`, `ignoreMouse`, etc.
     * See {@link https://floating-ui.com/docs/useClick}.
     */
    clickProps?: UseClickProps,

    /** Options forwarded to the internal `useUpdate` position-update utility. */
    updateProps?: UseUpdateOptions,
}

type NativeProps = ComponentProps<'div'>;

type ElementsOrNull = Array<HTMLElement | null>;
type LabelsOrNull = Array<string | null>;
export interface getFloatingElPropsReturnType extends NativeProps {
    /** Returns the current Floating UI `FloatingContext` for sub-hooks and child components. */
    getContext: () => FloatingContext,
    /** Ref array holding each registered list-item element for keyboard navigation. */
    listItemsRef: React.MutableRefObject<ElementsOrNull>,
    /** Ref array holding each list-item label string for typeahead matching. */
    labelsRef: React.MutableRefObject<LabelsOrNull>,
    /** Floating UI node ID used by `FloatingNode` for nested-panel tree registration. */
    nodeId: string | undefined,
}

export type UseFloatingReturnType = {
    /** Callback ref to attach to the anchor (trigger) element. */
    anchorRef: (node: Element | null) => void,
    /**
     * Merges all active interaction props (hover, click, focus, ARIA) onto an
     * arbitrary set of element props without overwriting caller-supplied handlers.
     * Spread the result onto your trigger element.
     */
    getAnchorProps: UseInteractionsReturn['getReferenceProps'],
    /** Callback ref to attach to the floating panel element. */
    floatingElRef: ReturnType<typeof useFloatingHook>['refs']['setFloating'],
    /**
     * Optional virtual reference callback. Attach to a child element (e.g. an icon)
     * when you want the floating panel to be positioned relative to that sub-element
     * rather than the full anchor bounding box.
     */
    positionRef: (node: ReferenceType | null) => void,
    /**
     * Merges Floating UI positioning styles and context props onto native `<div>`
     * props. Pass to `<FloatingPopover>` or use directly on a `<div>`.
     */
    getFloatingElProps: (props?: ComponentProps<'div'>) => getFloatingElPropsReturnType,
    /** Merges keyboard-navigation props onto each list-item element. */
    getItemProps: UseInteractionsReturn['getItemProps'],
    /** Returns the current Floating UI `FloatingContext`. */
    getContext: () => FloatingContext,
    /** Arrow component (or `null` when `arrowEnabled` is `false`). Render inside the floating panel. */
    arrow: UseArrowReturnType['Component'],
    /** Index of the currently keyboard-focused list item, or `null` when none. */
    activeIndex: number | null,
    /** Programmatically set the focused list-item index. */
    setActiveIndex: (index: number | null) => void,
};

/**
 * Basic hook for creating floating elements with Floating UI. Use it with FloatingPopover component.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/docs/utils-floating--documentation}
 * */
export const useFloating = ({
    maxHeight,
    maxWidth,
    verticalOffset = 0,
    horizontalOffset = 0,
    role,
    placement,
    allowedPlacements,
    type,
    strategy = type ? Strategies[type] : undefined,
    shown,
    setShown,
    hoverEnabled = false,
    clickEnabled = false,
    focusEnabled = false,
    typeaheadEnabled = false,
    middlewares = [],
    listNavigationEnabled = false,
    arrowEnabled = false,
    onToggle,
    equalWidth = false,
    listNavigationProps,
    hoverProps,
    dismissProps,
    clickProps,
    arrowProps,
    updateProps,
    ...props
}: UseFloatingProps): UseFloatingReturnType => {
    const [open, setOpen] = useControlledState(shown, setShown, false);

    const elementsRef = useRef<Array<HTMLElement | null>>([]);
    const labelsRef = useRef<Array<string | null>>([]);

    useComponentDidUpdate(() => {
        onToggle?.(open);
    }, [open]);

    const [activeIndex, setActiveIndex] = useControlledState(
        listNavigationProps?.activeIndex,
        listNavigationProps?.onNavigate,
        null,
    );

    const nodeId = useFloatingNodeId();

    const nested = useFloatingParentNodeId() !== null;

    const arrow = useArrow({
        enabled: arrowEnabled,
        ...arrowProps,
    });

    const {whileElementsMounted, contextRef} = useUpdate(setOpen, updateProps);

    const {refs, floatingStyles, context} = useFloatingHook({
        ...props,
        placement: placement || allowedPlacements?.[0],
        nodeId,
        strategy,
        open,
        onOpenChange: setOpen,
        whileElementsMounted,
        middleware: [
            offset({
                mainAxis: verticalOffset + arrow.height,
                crossAxis: horizontalOffset,
            }),
            flip({
                fallbackPlacements: allowedPlacements,
            }),
            shift(),
            size({
                apply: ({availableWidth, availableHeight, elements}) => {

                    Object.assign(elements.floating.style, {
                        maxWidth: `${
                            !equalWidth && maxWidth && maxWidth <= availableWidth
                                ? maxWidth
                                : availableWidth
                        }px`,
                        maxHeight: `${
                            maxHeight && maxHeight <= availableHeight
                                ? maxHeight
                                : availableHeight
                        }px`,
                    });

                    if (equalWidth) {
                        const {width} = elements.reference.getBoundingClientRect();
                        Object.assign(elements.floating.style, {
                            width: `${width}px`,
                        });
                    }
                },
            }),
            arrow.middleware,
            ...middlewares,
        ],
    });

    contextRef.current = context;

    const dismiss = useDismiss(context, dismissProps);

    const hover = useHover(context, {
        enabled: hoverEnabled,
        handleClose: safePolygon(),
        delay: {
            open: 150, // to prevent opening when moving mouse over a list of anchors (eg. in a menu)
        },
        ...hoverProps,
    });

    const click = useClick(context, {
        enabled: clickEnabled,
        ...clickProps,
    });

    const focus = useFocus(context, {
        enabled: focusEnabled,
    });

    const listNavigation = useListNavigation(context, {
        enabled: listNavigationEnabled,
        listRef: elementsRef,
        activeIndex,
        loop: true,
        onNavigate: setActiveIndex,
        nested,
        ...listNavigationProps,
    });

    const typeahead = useTypeahead(context, {
        enabled: typeaheadEnabled,
        listRef: labelsRef,
        activeIndex,
        onMatch: setActiveIndex,
    });

    const roleInteraction = useRole(context, {role});

    const {getReferenceProps, getFloatingProps, getItemProps} = useInteractions([
        dismiss,
        roleInteraction,
        listNavigation,
        typeahead,
        hover,
        click,
        focus,
    ]);

    return {
        anchorRef: refs.setReference,
        getAnchorProps: _props => ({
            ..._props,
            ...getReferenceProps(_props), // props is merged twice cause getReferenceProps returns only own props, not all props from props
        }),
        floatingElRef: refs.setFloating,
        positionRef: refs.setPositionReference,
        getItemProps: itemProps => ({
            ...itemProps,
            ...getItemProps(itemProps), // itemProps is merged twice cause getItemProps returns only own props, not all props from itemProps
        }),
        arrow: arrow.Component,
        activeIndex,
        setActiveIndex,
        getContext: () => context,
        getFloatingElProps: _props => ({
            ..._props,
            ...getFloatingProps(_props), // props is merged twice cause getFloatingProps returns only own props, not all props from props
            // for some magic reason context doesn't work if is propagated as an object
            getContext: () => context,
            style: {
                ...floatingStyles,
                ..._props?.style,
            },
            listItemsRef: elementsRef,
            labelsRef,
            nodeId,
        }),
    };
};
